import { useState } from 'react';
import { Card, Button, Table, IconButton, LoadingSpinner } from '../ui';
import {
  ArrowPathIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  useBackups,
  useCreateBackup,
  useDeleteBackup,
  useRestorePreview,
  useExecuteRestore,
} from '../../hooks/useBackups';
import api from '../../services/api';

// Must match functions/backup/backupService.js COLLECTIONS — single source
// of truth on the backend, mirrored here for the scope-selection UI only.
const COLLECTIONS = [
  'cms_users',
  'cms_groups',
  'companies',
  'company_phases',
  'company_documents',
  'company_activities',
  'engagement_templates',
  'company_surveys',
  'survey_responses',
  'company_responses',
  'databases',
  'upload_jobs',
  'benchmarks',
  'responses',
  'workshop_bookings',
];

function formatBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function StatusBadge({ status, errors }) {
  const map = {
    success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    partial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    running: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  const tooltip = Array.isArray(errors) && errors.length > 0
    ? errors.map((e) => `${e.collection || 'error'}: ${e.error}`).join('\n')
    : undefined;
  return (
    <span
      title={tooltip}
      className={`text-[11px] px-1.5 py-0 rounded font-medium ${cls} ${tooltip ? 'cursor-help underline decoration-dotted' : ''}`}
    >
      {status || 'unknown'}
    </span>
  );
}

export default function BackupsManager() {
  const { data: backups, isLoading, error } = useBackups();
  const createMutation = useCreateBackup();
  const deleteMutation = useDeleteBackup();

  const [view, setView] = useState({ mode: 'list', backup: null });

  if (view.mode === 'restore' && view.backup) {
    return <BackupRestoreFlow backup={view.backup} onClose={() => setView({ mode: 'list', backup: null })} />;
  }
  if (view.mode === 'delete' && view.backup) {
    return (
      <BackupDeleteConfirm
        backup={view.backup}
        onClose={() => setView({ mode: 'list', backup: null })}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(view.backup.id);
          setView({ mode: 'list', backup: null });
        }}
        deleting={deleteMutation.isPending}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">Backups</h2>
        <Button
          variant="primary"
          onClick={() => createMutation.mutate()}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create backup'}
        </Button>
      </div>

      {createMutation.isError && (
        <Card className="mb-4 border-red-300 dark:border-red-700">
          <div className="text-sm text-red-700 dark:text-red-400">
            Backup failed: {createMutation.error?.response?.data?.details || createMutation.error?.message}
          </div>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : error ? (
        <Card><div className="text-sm text-red-600">Failed to load backups.</div></Card>
      ) : !backups || backups.length === 0 ? (
        <Card>
          <div className="text-sm text-text-muted text-center py-6">
            No backups yet. Click <span className="font-medium">Create backup</span> to make your first snapshot.
          </div>
        </Card>
      ) : (
        <Card padding="none">
          <Table
            cellPadding="compact"
            headers={[
              { label: 'Date' },
              { label: 'Type' },
              { label: 'Status' },
              { label: 'Size', className: 'text-right' },
              { label: 'Docs', className: 'text-right' },
              { label: 'Auth', className: 'text-right' },
              { label: 'Drive' },
              { label: 'Actions', className: 'text-right' },
            ]}
            rows={backups.map((b) => ({
              cells: [
                { content: formatDate(b.createdAt), className: 'text-sm text-text-primary-light dark:text-text-primary' },
                { content: <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">{b.source || '—'}</span> },
                { content: <StatusBadge status={b.status} errors={b.errors} /> },
                { content: formatBytes(b.sizeBytes), className: 'text-right text-sm text-text-muted' },
                { content: b.documentCount ?? '—', className: 'text-right text-sm text-text-muted' },
                { content: b.authUserCount ?? '—', className: 'text-right text-sm text-text-muted' },
                {
                  content: b.driveWebViewLink ? (
                    <a
                      href={b.driveWebViewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-primary hover:underline text-sm"
                    >
                      open
                    </a>
                  ) : <span className="text-text-muted text-sm">—</span>,
                },
                {
                  className: 'text-right',
                  content: (
                    <div className="flex justify-end gap-1">
                      <BackupDownloadButton id={b.id} disabled={b.status === 'failed' || b.status === 'running'} />
                      <IconButton
                        icon={ArrowUturnLeftIcon}
                        label="Restore"
                        size="sm"
                        variant="ghost"
                        disabled={b.status === 'failed' || b.status === 'running'}
                        onClick={() => setView({ mode: 'restore', backup: b })}
                      />
                      <IconButton
                        icon={TrashIcon}
                        label="Delete"
                        size="sm"
                        variant="ghost"
                        onClick={() => setView({ mode: 'delete', backup: b })}
                      />
                    </div>
                  ),
                },
              ],
            }))}
          />
        </Card>
      )}
    </div>
  );
}

// Fetches a fresh signed download URL on click. Signed URLs expire after
// 60 min, so always fetch a new one rather than caching stale URLs in state.
function BackupDownloadButton({ id, disabled }) {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/cms/backups/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${id}.json.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert('Download failed: ' + (e?.response?.data?.details || e?.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <IconButton
      icon={ArrowDownTrayIcon}
      label="Download"
      size="sm"
      variant="ghost"
      disabled={disabled || busy}
      onClick={onClick}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Restore flow: scope → preview → type-to-confirm → execute
// ─────────────────────────────────────────────────────────────────
function BackupRestoreFlow({ backup, onClose }) {
  const [step, setStep] = useState('scope'); // 'scope' | 'preview' | 'done'
  const [collections, setCollections] = useState(COLLECTIONS);
  const [includeAuth, setIncludeAuth] = useState(true);
  const [confirmText, setConfirmText] = useState('');

  const previewMutation = useRestorePreview();
  const executeMutation = useExecuteRestore();

  const toggleCollection = (name) => {
    setCollections((prev) =>
      prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]
    );
  };

  const allSelected = collections.length === COLLECTIONS.length && includeAuth;
  const toggleAll = () => {
    if (allSelected) {
      setCollections([]);
      setIncludeAuth(false);
    } else {
      setCollections(COLLECTIONS);
      setIncludeAuth(true);
    }
  };

  const handlePreview = async () => {
    await previewMutation.mutateAsync({ id: backup.id, collections, includeAuth });
    setStep('preview');
  };

  const handleExecute = async () => {
    const token = previewMutation.data?.confirmToken;
    if (!token) return;
    await executeMutation.mutateAsync({
      id: backup.id,
      collections,
      includeAuth,
      confirmToken: token,
    });
    setStep('done');
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="secondary" size="sm" onClick={onClose}>← Back to backups</Button>
        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
          Restore from {backup.id}
        </h2>
      </div>

      <Card className="mb-4 border-amber-300 dark:border-amber-700">
        <div className="flex gap-2 items-start">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium text-text-primary-light dark:text-text-primary mb-1">
              Non-destructive restore
            </div>
            <div className="text-text-muted">
              Documents will be upserted by ID. Anything created or modified <em>after</em> this backup will be left untouched
              unless its ID matches a doc in the backup (in which case it will be overwritten).
              Existing documents NOT in the backup are never deleted.
            </div>
          </div>
        </div>
      </Card>

      {step === 'scope' && (
        <Card>
          <h3 className="text-base font-semibold mb-4">Choose what to restore</h3>
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
                className="rounded border-border"
              />
              <span className="font-medium">Select all</span>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {COLLECTIONS.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={collections.includes(c)}
                  onChange={() => toggleCollection(c)}
                  className="rounded border-border"
                />
                <span>{c}</span>
              </label>
            ))}
            <label className="flex items-center gap-2 text-sm cursor-pointer col-span-2 pt-2 border-t border-border">
              <input
                type="checkbox"
                checked={includeAuth}
                onChange={(e) => setIncludeAuth(e.target.checked)}
                className="rounded border-border"
              />
              <span className="font-medium">Firebase Auth users</span>
              <span className="text-xs text-text-muted">({backup.authUserCount ?? 0} users)</span>
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handlePreview}
              disabled={(collections.length === 0 && !includeAuth) || previewMutation.isPending}
            >
              {previewMutation.isPending ? 'Calculating...' : 'Preview restore'}
            </Button>
          </div>
          {previewMutation.isError && (
            <div className="mt-3 text-sm text-red-600">
              Preview failed: {previewMutation.error?.response?.data?.details || previewMutation.error?.message}
            </div>
          )}
        </Card>
      )}

      {step === 'preview' && previewMutation.data && (
        <Card>
          <h3 className="text-base font-semibold mb-4">Preview</h3>
          <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Firestore</div>
          <Table
            cellPadding="compact"
            headers={[
              { label: 'Collection' },
              { label: 'In backup', className: 'text-right' },
              { label: 'Overwrite', className: 'text-right' },
              { label: 'Recreate (was deleted)', className: 'text-right' },
              { label: 'Untouched (newer)', className: 'text-right' },
            ]}
            rows={Object.entries(previewMutation.data.preview.perCollection || {}).map(([name, c]) => ({
              cells: [
                { content: name, className: 'text-sm' },
                { content: c.backupCount, className: 'text-right text-sm' },
                { content: c.overwrite, className: 'text-right text-sm' },
                { content: c.recreate, className: 'text-right text-sm' },
                { content: c.untouched, className: 'text-right text-sm text-text-muted' },
              ],
            }))}
          />

          {previewMutation.data.preview.perAuth && (
            <>
              <div className="text-xs uppercase tracking-wider text-text-muted mt-6 mb-2">Firebase Auth</div>
              <div className="text-sm">
                Will import <span className="font-medium">{previewMutation.data.preview.perAuth.willImport}</span> users
                from backup.{' '}
                <span className="text-text-muted">
                  Current Auth count: {previewMutation.data.preview.perAuth.currentCount}.
                  Existing UIDs are merged; nothing is deleted.
                </span>
              </div>
            </>
          )}

          <div className="mt-6 pt-4 border-t border-border">
            <label className="block text-sm font-medium mb-2">
              Type <span className="font-mono bg-gray-100 dark:bg-background-gray px-1 rounded">RESTORE</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
              placeholder="RESTORE"
              autoFocus
            />
          </div>

          <div className="flex gap-2 justify-end mt-4">
            <Button variant="secondary" onClick={() => setStep('scope')}>← Change scope</Button>
            <Button
              variant="danger"
              onClick={handleExecute}
              disabled={confirmText !== 'RESTORE' || executeMutation.isPending}
            >
              {executeMutation.isPending ? 'Restoring...' : 'Execute restore'}
            </Button>
          </div>

          {executeMutation.isError && (
            <div className="mt-3 text-sm text-red-600">
              Restore failed: {executeMutation.error?.response?.data?.details || executeMutation.error?.message}
            </div>
          )}
        </Card>
      )}

      {step === 'done' && executeMutation.data && (
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <ArrowPathIcon className="w-5 h-5 text-brand-primary" />
            <h3 className="text-base font-semibold">Restore complete</h3>
          </div>
          <div className="text-sm space-y-1 mb-4">
            {Object.entries(executeMutation.data.written || {}).map(([name, n]) => (
              <div key={name}>
                <span className="font-mono text-xs">{name}</span>: <span className="font-medium">{n}</span> document(s) written
              </div>
            ))}
            {executeMutation.data.authWritten > 0 && (
              <div><span className="font-mono text-xs">__auth__</span>: <span className="font-medium">{executeMutation.data.authWritten}</span> user(s) imported</div>
            )}
          </div>
          {executeMutation.data.errors?.length > 0 && (
            <div className="mb-4 text-sm">
              <div className="font-medium text-amber-700 dark:text-amber-400 mb-1">Errors:</div>
              <ul className="list-disc list-inside text-text-muted">
                {executeMutation.data.errors.map((e, i) => (
                  <li key={i}>{e.collection}: {e.error}</li>
                ))}
              </ul>
            </div>
          )}
          <Button variant="primary" onClick={onClose}>Done</Button>
        </Card>
      )}
    </div>
  );
}

function BackupDeleteConfirm({ backup, onClose, onConfirm, deleting }) {
  const [confirmText, setConfirmText] = useState('');
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <Button variant="secondary" size="sm" onClick={onClose}>← Back to backups</Button>
        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary">
          Delete backup
        </h2>
      </div>
      <Card className="border-red-300 dark:border-red-700 max-w-xl">
        <div className="flex gap-2 items-start mb-4">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-text-primary-light dark:text-text-primary mb-1">
              Permanently delete this backup?
            </div>
            <div className="text-sm text-text-muted">
              This removes the file from Cloud Storage AND Google Drive, and the metadata row.
              This cannot be undone.
            </div>
          </div>
        </div>
        <div className="text-sm mb-3">
          <span className="text-text-muted">Backup:</span>{' '}
          <span className="font-mono">{backup.id}</span>
        </div>
        <label className="block text-sm font-medium mb-2">
          Type <span className="font-mono bg-gray-100 dark:bg-background-gray px-1 rounded">DELETE</span> to confirm
        </label>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary mb-4"
          placeholder="DELETE"
          autoFocus
        />
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={confirmText !== 'DELETE' || deleting}
          >
            {deleting ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
